import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { authApi, companiesApi } from '../../lib/api';
import { Field, Select, TextInput } from '../../components/Field';
import { ErrorBanner } from '../../components/ErrorBanner';
import { FEATURE_META } from '../../lib/features';
import { FEATURE_KEYS } from '../../lib/types';
import type { BusinessProfile, EntityType, FeatureKey } from '../../lib/types';
import { useCompanyStore } from '../../stores/company';
import { useSessionStore } from '../../stores/session';
import { useUiStore } from '../../stores/ui';
import { usePermission } from '../../lib/permissions';

interface ProfilePreview {
  key: BusinessProfile;
  label: string;
  blurb: string;
  on: FeatureKey[];
}

// Mirrors docs/api-phase0.md's business-profile preset table — display copy
// only; the server computes the authoritative toggle set on POST /companies.
const PROFILES: ProfilePreview[] = [
  { key: 'trader', label: 'Trader', blurb: 'Buys and sells goods, no manufacturing.', on: ['maintain_inventory', 'bill_wise_details', 'discount_columns', 'additional_charges', 'gst_enabled'] },
  { key: 'distributor', label: 'Distributor', blurb: 'Multi-location stock, batch tracking, price lists.', on: ['maintain_inventory', 'multi_godown', 'batches_and_expiry', 'bill_wise_details', 'order_processing', 'multiple_price_levels', 'discount_columns', 'additional_charges', 'gst_enabled'] },
  { key: 'manufacturer', label: 'Manufacturer', blurb: 'BOM, job work, serials, cost centres — the full set.', on: ['maintain_inventory', 'multi_godown', 'batches_and_expiry', 'serial_tracking', 'bill_wise_details', 'cost_centres', 'bom_manufacturing', 'job_work', 'order_processing', 'actual_vs_billed_qty', 'discount_columns', 'additional_charges', 'gst_enabled'] },
  { key: 'job_worker', label: 'Job worker', blurb: 'Material in/out, actual vs billed quantity.', on: ['maintain_inventory', 'multi_godown', 'bill_wise_details', 'cost_centres', 'job_work', 'actual_vs_billed_qty', 'additional_charges', 'gst_enabled'] },
  { key: 'services', label: 'Services', blurb: 'Accounts-only — no item/stock fields anywhere.', on: ['bill_wise_details', 'cost_centres', 'gst_enabled'] },
  { key: 'retailer', label: 'Retailer', blurb: 'Single location, simple stock, no bill-wise ageing.', on: ['maintain_inventory', 'discount_columns', 'gst_enabled'] },
];

const ENTITY_TYPES: { value: EntityType; label: string }[] = [
  { value: 'proprietorship', label: 'Proprietorship' },
  { value: 'partnership', label: 'Partnership' },
  { value: 'llp', label: 'LLP' },
  { value: 'private_limited', label: 'Private limited' },
  { value: 'public_limited', label: 'Public limited' },
  { value: 'huf', label: 'HUF' },
  { value: 'trust', label: 'Trust' },
  { value: 'society', label: 'Society' },
  { value: 'aop_boi', label: 'AOP / BOI' },
];

const STATES = [
  { value: '27', label: 'Maharashtra (27)' }, { value: '29', label: 'Karnataka (29)' },
  { value: '33', label: 'Tamil Nadu (33)' }, { value: '07', label: 'Delhi (07)' },
  { value: '24', label: 'Gujarat (24)' }, { value: '09', label: 'Uttar Pradesh (09)' },
  { value: '19', label: 'West Bengal (19)' }, { value: '06', label: 'Haryana (06)' },
  { value: '08', label: 'Rajasthan (08)' }, { value: '32', label: 'Kerala (32)' },
];

const STEPS = ['Identity', 'Business profile', 'Primary branch'] as const;

function defaultBooksBeginOn(): string {
  const today = new Date();
  const year = today.getMonth() >= 3 ? today.getFullYear() : today.getFullYear() - 1;
  return `${year}-04-01`;
}

export function CompanyCreateWizard() {
  const [step, setStep] = useState(0);
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const canCreateCompany = usePermission()('company:company:create');
  const pushToast = useUiStore((s) => s.pushToast);
  const setActiveCompanyId = useCompanyStore((s) => s.setActiveCompanyId);
  const setCompanies = useCompanyStore((s) => s.setCompanies);
  const setFeatures = useCompanyStore((s) => s.setFeatures);
  const companies = useCompanyStore((s) => s.companies);
  const setSession = useSessionStore((s) => s.setSession);

  const [name, setName] = useState(() => searchParams.get('name')?.trim() ?? '');
  const [legalName, setLegalName] = useState(() => searchParams.get('name')?.trim() ?? '');
  const [pan, setPan] = useState('');
  const [entityType, setEntityType] = useState<EntityType>('proprietorship');
  const [booksBeginOn, setBooksBeginOn] = useState(defaultBooksBeginOn);

  const [businessProfile, setBusinessProfile] = useState<BusinessProfile>('trader');

  const [branchName, setBranchName] = useState('Head Office');
  const [gstin, setGstin] = useState('');
  const [stateCode, setStateCode] = useState('');
  const [registrationType, setRegistrationType] = useState<'regular' | 'composition' | 'unregistered' | 'sez' | 'sez_developer'>('unregistered');
  const [addressLine1, setAddressLine1] = useState('');
  const [addressLine2, setAddressLine2] = useState('');
  const [city, setCity] = useState('');
  const [pincode, setPincode] = useState('');

  function next(e: React.FormEvent) {
    e.preventDefault();
    setStep((s) => Math.min(s + 1, STEPS.length - 1));
  }

  function back() {
    setStep((s) => Math.max(s - 1, 0));
  }

  async function handleFinish(e: React.FormEvent) {
    e.preventDefault();
    if (!canCreateCompany) {
      setError(new Error('You do not have company:company:create permission. Ask an administrator to create the company or grant this permission.'));
      return;
    }
    if (!stateCode) {
      setError(new Error('Select the primary branch state before creating the company.'));
      return;
    }
    if (!gstin && registrationType !== 'unregistered') {
      setError(new Error('Enter a GSTIN for a registered branch, or choose Unregistered.'));
      return;
    }
    if (gstin && registrationType === 'unregistered') {
      setError(new Error('Choose the applicable GST registration type for this GSTIN.'));
      return;
    }
    const registered = registrationType !== 'unregistered';
    if (registered && !addressLine1.trim()) {
      setError(new Error('Enter the registered business address. It is printed as the issuing address on tax invoices.'));
      return;
    }
    if (registered && !city.trim()) {
      setError(new Error('Enter the city for the registered business address.'));
      return;
    }
    if (registered && !/^\d{6}$/.test(pincode.trim())) {
      setError(new Error('Enter a valid 6-digit PIN for the registered business address.'));
      return;
    }
    setError(null);
    setBusy(true);
    try {
      const created = await companiesApi.create({
        name, legalName, pan: pan || null, entityType, booksBeginOn, businessProfile, country: 'IN',
        primaryBranch: {
          name: branchName,
          gstin: gstin || null,
          stateCode,
          registrationType,
          addressLine1: addressLine1.trim() || null,
          addressLine2: addressLine2.trim() || null,
          city: city.trim() || null,
          pincode: pincode.trim() || null,
        },
      });
      // Select the new company before refreshing the session so the request carries
      // the new X-Company-Id and the server resolves its permissions/restrictions.
      setActiveCompanyId(created.company.id);
      try {
        const session = await authApi.session();
        setSession(session);
        setCompanies(session.companies);
        setActiveCompanyId(session.activeCompanyId ?? created.company.id);
        setFeatures(created.features);
      } catch (sessionError) {
        // Creation is already committed server-side; never invite a retry that
        // would create a duplicate. Keep the new company visible and explain the
        // recovery action instead.
        setCompanies([...companies, { id: created.company.id, name: created.company.name, isActive: true }]);
        setFeatures(created.features);
        setError(new Error(`Company created, but the session could not refresh. Reload the page before continuing. ${sessionError instanceof Error ? sessionError.message : ''}`.trim()));
        return;
      }
      pushToast({ kind: 'success', message: `${created.company.name} created.` });
      navigate('/onboarding', { replace: true });
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  }

  const profile = PROFILES.find((p) => p.key === businessProfile)!;
  const requiresRegisteredAddress = registrationType !== 'unregistered';

  if (!canCreateCompany) {
    return (
      <div className="flex h-screen items-center justify-center bg-[var(--bg)] px-4">
        <div className="w-full max-w-lg border border-[var(--border-strong)] bg-[var(--bg-elevated)] p-5">
          <p className="mono text-[10px] uppercase tracking-[0.18em] text-[var(--accent)]">Company setup</p>
          <h1 className="mt-1 text-[16px] font-semibold">Company creation is restricted</h1>
          <p className="mt-2 text-[12.5px] text-[var(--text-dim)]">Ask an administrator to grant <span className="mono">company:company:create</span>. Your current books remain unchanged.</p>
          <button className="button-muted mt-4" onClick={() => navigate(-1)}>Go back</button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen items-start justify-center overflow-y-auto bg-[var(--bg)] px-4 py-8">
      <div className="w-full max-w-xl">
        <div className="mb-4 flex items-center gap-3 border-b border-[var(--border)] pb-3">
          <h1 className="text-[15px] font-semibold">New company</h1>
          <div className="flex gap-1 text-[11px] text-[var(--text-faint)]">
            {STEPS.map((s, i) => (
              <span key={s} className={i === step ? 'font-medium text-[var(--accent)]' : undefined}>
                {i > 0 && <span className="px-1">›</span>}
                {s}
              </span>
            ))}
          </div>
        </div>

        {error ? <ErrorBanner error={error} onDismiss={() => setError(null)} /> : null}

        {step === 0 && (
          <form onSubmit={next} className="mt-3 flex flex-col gap-3">
            <Field label="Company name" htmlFor="w-name" hint="The short name operators will see in the company switcher." required>
              <TextInput id="w-name" autoFocus value={name} onChange={(e) => setName(e.target.value)} required />
            </Field>
            <Field label="Legal name" htmlFor="w-legal" hint="The registered name used on formal reports." required>
              <TextInput id="w-legal" value={legalName} onChange={(e) => setLegalName(e.target.value)} required />
            </Field>
            <Field label="PAN" htmlFor="w-pan" hint="10-character PAN, optional at this stage.">
              <TextInput id="w-pan" value={pan} onChange={(e) => setPan(e.target.value.toUpperCase())} maxLength={10} />
            </Field>
            <Field label="Entity type" htmlFor="w-entity" required>
              <Select id="w-entity" value={entityType} onChange={(e) => setEntityType(e.target.value as EntityType)}>
                {ENTITY_TYPES.map((t) => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </Select>
            </Field>
            <Field label="Books begin on" htmlFor="w-begin" hint="This anchors the first financial year. You can enter opening balances from this date onward." required>
              <TextInput id="w-begin" type="date" value={booksBeginOn} onChange={(e) => setBooksBeginOn(e.target.value)} required />
            </Field>
            <div className="mt-2 flex justify-end">
              <button type="submit" className="rounded-[3px] border border-[var(--accent)] bg-[var(--accent)] px-3 py-1.5 text-[12.5px] font-medium text-[var(--accent-fg)]">
                Next →
              </button>
            </div>
          </form>
        )}

        {step === 1 && (
          <form onSubmit={next} className="mt-3 flex flex-col gap-2">
            <p className="text-[11.5px] text-[var(--text-faint)]">
              Sets a sensible starting combination of the 16 feature toggles — fully overridable afterwards in Features (F11).
            </p>
            <div role="radiogroup" aria-label="Business profile" className="flex flex-col gap-1">
              {PROFILES.map((p) => (
                <label
                  key={p.key}
                  className={`flex cursor-pointer flex-col gap-0.5 rounded-[3px] border px-2.5 py-1.5 ${
                    p.key === businessProfile ? 'border-[var(--accent)] bg-[var(--bg-selected)]/40' : 'border-[var(--border)] hover:bg-[var(--bg-hover)]'
                  }`}
                >
                  <span className="flex items-center gap-2">
                    <input
                      type="radio"
                      name="business-profile"
                      value={p.key}
                      checked={p.key === businessProfile}
                      onChange={() => setBusinessProfile(p.key)}
                    />
                    <span className="text-[12.5px] font-medium">{p.label}</span>
                    <span className="text-[11.5px] text-[var(--text-faint)]">{p.blurb}</span>
                  </span>
                </label>
              ))}
            </div>

            <div className="mt-2 border border-[var(--border)] bg-[var(--bg-inset)] p-2">
              <p className="mb-1 text-[11px] font-medium uppercase tracking-wide text-[var(--text-dim)]">{profile.label} turns on</p>
              <div className="flex flex-wrap gap-1">
                {FEATURE_KEYS.map((k) => {
                  const on = profile.on.includes(k);
                  return (
                    <span
                      key={k}
                      className={`rounded-[2px] border px-1.5 py-0.5 text-[10.5px] ${
                        on ? 'border-[var(--success)] text-[var(--success)]' : 'border-[var(--border)] text-[var(--text-faint)] line-through'
                      }`}
                    >
                      {FEATURE_META[k].label}
                    </span>
                  );
                })}
              </div>
            </div>

            <div className="mt-2 flex justify-between">
              <button type="button" onClick={back} className="rounded-[3px] border border-[var(--border)] px-3 py-1.5 text-[12.5px]">
                ← Back
              </button>
              <button type="submit" className="rounded-[3px] border border-[var(--accent)] bg-[var(--accent)] px-3 py-1.5 text-[12.5px] font-medium text-[var(--accent-fg)]">
                Next →
              </button>
            </div>
          </form>
        )}

        {step === 2 && (
          <form onSubmit={handleFinish} className="mt-3 flex flex-col gap-3">
            <Field label="Branch name" htmlFor="w-branch-name" required>
              <TextInput id="w-branch-name" autoFocus value={branchName} onChange={(e) => setBranchName(e.target.value)} required />
            </Field>
            <Field label="GSTIN" htmlFor="w-gstin" hint="Leave blank if unregistered.">
              <TextInput
                id="w-gstin"
                value={gstin}
                onChange={(e) => {
                  const next = e.target.value.toUpperCase();
                  setGstin(next);
                  // The default is intentionally Unregistered. The moment a GSTIN is
                  // entered we select the ordinary registered classification; users can
                  // still choose Composition or SEZ explicitly where applicable.
                  if (next && registrationType === 'unregistered') setRegistrationType('regular');
                  if (!next && registrationType !== 'unregistered') setRegistrationType('unregistered');
                }}
                maxLength={15}
              />
            </Field>
            <Field label="State" htmlFor="w-state" required hint="Confirm State and registration type: they determine GST treatment.">
              <Select id="w-state" value={stateCode} onChange={(e) => setStateCode(e.target.value)} required>
                <option value="" disabled>Select state</option>
                {STATES.map((s) => (
                  <option key={s.value} value={s.value}>{s.label}</option>
                ))}
              </Select>
            </Field>
            <Field label="Registration type" htmlFor="w-reg" required>
              <Select id="w-reg" value={registrationType} onChange={(e) => setRegistrationType(e.target.value as typeof registrationType)}>
                <option value="regular">Regular</option>
                <option value="composition">Composition</option>
                <option value="unregistered" disabled={Boolean(gstin)}>Unregistered</option>
                <option value="sez">SEZ</option>
                <option value="sez_developer">SEZ developer</option>
              </Select>
            </Field>

            {requiresRegisteredAddress ? (
              <section className="border border-[var(--border)] bg-[var(--bg-inset)] p-3" aria-labelledby="w-issuer-address-heading">
                <div className="mb-2 flex items-baseline justify-between gap-3">
                  <div>
                    <h2 id="w-issuer-address-heading" className="text-[12.5px] font-medium">Registered business address</h2>
                    <p className="mt-0.5 text-[11px] text-[var(--text-faint)]">Printed as your issuing address on tax invoices. Country: India.</p>
                  </div>
                  <span className="mono shrink-0 text-[10.5px] text-[var(--text-faint)]">IN</span>
                </div>
                <div className="flex flex-col gap-3">
                  <Field label="Address line 1" htmlFor="w-address-line-1" required>
                    <TextInput id="w-address-line-1" value={addressLine1} onChange={(e) => setAddressLine1(e.target.value)} required={requiresRegisteredAddress} placeholder="Building, street, locality" />
                  </Field>
                  <Field label="Address line 2" htmlFor="w-address-line-2" hint="Optional — floor, landmark or area.">
                    <TextInput id="w-address-line-2" value={addressLine2} onChange={(e) => setAddressLine2(e.target.value)} />
                  </Field>
                  <div className="grid grid-cols-[1fr_8.5rem] gap-2">
                    <Field label="City" htmlFor="w-city" required>
                      <TextInput id="w-city" value={city} onChange={(e) => setCity(e.target.value)} required={requiresRegisteredAddress} />
                    </Field>
                    <Field label="PIN" htmlFor="w-pincode" required hint="6 digits">
                      <TextInput id="w-pincode" value={pincode} onChange={(e) => setPincode(e.target.value.replace(/\D/g, '').slice(0, 6))} inputMode="numeric" autoComplete="postal-code" pattern="[0-9]{6}" required={requiresRegisteredAddress} placeholder="400001" />
                    </Field>
                  </div>
                </div>
              </section>
            ) : (
              <p className="border border-dashed border-[var(--border)] bg-[var(--bg-inset)] px-3 py-2 text-[11.5px] text-[var(--text-faint)]">
                Unregistered business? You can create the company now and add an address later if you need it on bills or reports.
              </p>
            )}

            <div className="mt-2 flex justify-between">
              <button type="button" onClick={back} className="rounded-[3px] border border-[var(--border)] px-3 py-1.5 text-[12.5px]">
                ← Back
              </button>
              <button
                type="submit"
                disabled={busy}
                className="rounded-[3px] border border-[var(--accent)] bg-[var(--accent)] px-3 py-1.5 text-[12.5px] font-medium text-[var(--accent-fg)] disabled:opacity-50"
              >
                {busy ? 'Creating…' : 'Create company'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
